/* eslint-disable @typescript-eslint/no-namespace */
import moment from 'moment';
import { mapEnums } from '../utils/mapping';
import {
  BenchmarkCurvePoint,
  BenchmarkCurve,
  MMDRatingType,
  MACRatingType,
  BenchmarkProvider,
} from '../types/benchmark.types';
import { getIdentifierForBenchmark } from '../helpers/benchmark-curve';

export namespace MACV1Schema {
  export enum RatingType {
    tripleA = 'Current',
    interpolated = 'Interpolated',
  }

  export interface Curve {
    date: string;
    ratingType: RatingType;
    data: BenchmarkCurvePoint[];
  }

  export function toModel(data: Curve[]): BenchmarkCurve[] {
    return data.map((curve) => {
      const ratingType = mapEnums(RatingType, MACRatingType, curve.ratingType);
      const identifier = getIdentifierForBenchmark(
        BenchmarkProvider.mac,
        ratingType
      );

      return {
        date: moment(curve.date, 'YYYY-MM-DDZ'),
        identifier,
        data: curve.data,
      };
    });
  }
}

export namespace MMDV1Schema {
  export enum RatingType {
    tripleA = 'AAA',
    preRefunded = 'PRER',
    tripleAInsured = 'AAAI',
    doubleA = 'AA',
    singleA = 'A',
    bDoubleA = 'BAA',
    interpolated = 'INT',
  }

  export interface Curve {
    date: string;
    ratingType: RatingType;
    data: BenchmarkCurvePoint[];
  }

  export function toWeeklyModel(curves: Curve[]): BenchmarkCurve[] {
    const dates: string[] = curves
      .map((c) => c.date)
      .filter((val, idx, arr) => arr.indexOf(val) === idx);
    return dates.reduce((acc, val) => {
      const covertedCurves = toModel(
        curves.filter((curve) => curve.date === val)
      );
      return [...acc, ...covertedCurves];
    }, []);
  }

  export function toModel(data: Curve[]): BenchmarkCurve[] {
    const tripleACurve = data.find(
      (curve) => curve.ratingType === RatingType.tripleA
    );
    const interpCurve = data.find(
      (curve) => curve.ratingType === RatingType.interpolated
    );

    if (tripleACurve && interpCurve) {
      const lastYearInInterp = interpCurve.data.sort(
        (a, b) => b.year - a.year
      )[0].year;

      const tripleAFillerData = tripleACurve.data.filter(
        (point) => point.year > lastYearInInterp
      );

      interpCurve.data = [...interpCurve.data, ...tripleAFillerData];
    }

    return data.map((curve) => {
      const isInterp = curve.ratingType === RatingType.interpolated;
      const localCurve = isInterp ? interpCurve : curve;
      const ratingType = mapEnums(RatingType, MMDRatingType, curve.ratingType);
      const identifier = getIdentifierForBenchmark(
        BenchmarkProvider.mmd,
        ratingType
      );
      return {
        date: moment.utc(localCurve.date as any),
        identifier,
        data: localCurve.data,
      };
    });
  }
}
